import React, { useEffect, useState } from "react";
import {
  Row, Col,
  Spinner, Nav, NavItem, NavLink,
  TabContent, TabPane, Table
} from "reactstrap";
import classnames from "classnames";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ─── Card style — CSS variables, works in dark + light mode ─── */
const cardStyle = {
  background:   "var(--vz-secondary-bg, #fff)",
  border:       "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow:    "0 1px 4px rgba(0,0,0,0.06)",
  marginBottom: 12,
  overflow:     "hidden",
};

const sectionHeaderStyle = {
  padding:        "10px 14px",
  borderBottom:   "1px solid var(--vz-border-color)",
  background:     "linear-gradient(to right, #d87844, #f7c9a8)",
  display:        "flex",
  alignItems:     "center",
  gap:            8,
};

/* ─── Transparent badge helper ─────────────────────────────────── */
const BADGE_STYLES = {
  success:  { color: "#16a34a", bg: "rgba(22,163,74,.12)",   border: "rgba(22,163,74,.3)"  },
  danger:   { color: "#dc2626", bg: "rgba(220,38,38,.12)",   border: "rgba(220,38,38,.3)"  },
  warning:  { color: "#d97706", bg: "rgba(217,119,6,.12)",   border: "rgba(217,119,6,.3)"  },
  primary:  { color: "var(--vz-primary)", bg: "rgba(var(--vz-primary-rgb),.12)", border: "rgba(var(--vz-primary-rgb),.3)" },
  info:     { color: "#0284c7", bg: "rgba(2,132,199,.12)",   border: "rgba(2,132,199,.3)"  },
  secondary:{ color: "var(--vz-secondary-color)", bg: "rgba(var(--vz-secondary-rgb),.12)", border: "var(--vz-border-color)" },
};

function Pill({ variant = "secondary", children, className }) {
  const s = BADGE_STYLES[variant] || BADGE_STYLES.secondary;
  return (
    <span className={className} style={{
      display:      "inline-flex", alignItems: "center",
      padding:      "2px 10px", borderRadius: 20,
      fontSize:     11, fontWeight: 600,
      color:        s.color,
      background:   s.bg,
      border:       `1px solid ${s.border}`,
      textTransform:"uppercase", letterSpacing: ".04em",
    }}>
      {children}
    </span>
  );
}

/* ─── Auth helpers ──────────────────────────────────────────────── */
const getAuthHeaders = () => {
  try {
    const u = JSON.parse(sessionStorage.getItem("authUser"));
    return { "Content-Type": "application/json", ...(u?.token && { Authorization: `Bearer ${u.token}` }) };
  } catch { return { "Content-Type": "application/json" }; }
};

const safeJSON = (v) => {
  if (!v) return null;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return null; }
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "medium" }); }
  catch { return String(d); }
};

const val = (v) => (v !== null && v !== undefined && v !== "" ? v : "—");

/* safeFetch — never throws */
const safeFetch = async (url, headers) => {
  try {
    const r = await fetch(url, { headers });
    if (!r.ok) {
      console.warn(`[WindowsDetails] ${url} → HTTP ${r.status}`);
      return null;
    }
    const j = await r.json();
    return j?.success ? j : null;
  } catch (e) {
    console.error(`[WindowsDetails] fetch error ${url}:`, e);
    return null;
  }
};

/* ─── Sub-components ────────────────────────────────────────────── */

function DR({ label, children, mono }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <tr>
      <td style={{
        width: "200px", color: "var(--vz-secondary-color)",
        fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: ".05em",
        padding: "9px 14px", fontWeight: 600, verticalAlign: "top",
        borderBottom: "1px solid var(--vz-border-color)",
      }}>{label}</td>
      <td style={{
        padding: "9px 14px", fontSize: "0.875rem", verticalAlign: "top",
        color: "var(--vz-body-color)",
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: "break-all", borderBottom: "1px solid var(--vz-border-color)",
      }}>{children}</td>
    </tr>
  );
}

function Section({ title, icon, children }) {
  return (
    <div style={cardStyle}>
      <div style={sectionHeaderStyle}>
        {icon && <i className={icon} style={{ color: "#fff", fontSize: 14 }} />}
        <span style={{ fontWeight: 600, color: "#fff", fontSize: 13 }}>{title}</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <Table responsive borderless className="mb-0" style={{ fontSize: "0.875rem" }}>
          <tbody>{children}</tbody>
        </Table>
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="text-center py-5" style={{ color: "var(--vz-secondary-color)" }}>
      <i className="ri-inbox-line fs-2 d-block mb-2" style={{ opacity: .35 }} />
      <small>{msg || "No data available for this alert"}</small>
    </div>
  );
}

function Hash({ value }) {
  if (!value) return <span style={{ color: "var(--vz-secondary-color)" }}>—</span>;
  return <code style={{ fontSize: "0.78rem", color: "var(--vz-success)", wordBreak: "break-all" }}>{value}</code>;
}

function ResultBadge({ result }) {
  if (!result) return <span style={{ color: "var(--vz-secondary-color)" }}>—</span>;
  const r = result.toLowerCase();
  const v = r === "passed" ? "success" : r === "failed" ? "danger" : "warning";
  return <Pill variant={v}>{result}</Pill>;
}

function EventBadge({ event }) {
  if (!event) return <span style={{ color: "var(--vz-secondary-color)" }}>—</span>;
  const e = event.toLowerCase();
  const v = e === "added" ? "success" : e === "deleted" ? "danger" : "warning";
  return <Pill variant={v}>{event}</Pill>;
}

function SevBadge({ severity }) {
  if (!severity) return <span style={{ color: "var(--vz-secondary-color)" }}>—</span>;
  const s = severity.toLowerCase();
  const v = s === "critical" ? "danger" : s === "high" ? "warning" : s === "medium" ? "info" : "success";
  return <Pill variant={v}>{severity}</Pill>;
}

/* ─── Geo Panel ─────────────────────────────────────────────────── */
function GeoLocationPanel({ geo, agentIp }) {
  if (!geo) {
    return (
      <Empty msg={
        agentIp
          ? `IP ${agentIp} is a private/internal address — geolocation is not available.`
          : "No agent IP available for geolocation lookup."
      } />
    );
  }

  return (
    <>
      <div style={{ ...cardStyle, padding: "16px 18px", marginBottom: 12 }}>
        <Row className="align-items-center">
          <Col md={1} className="text-center">
            {geo.countryCode ? (
              <img
                src={`https://flagcdn.com/48x36/${geo.countryCode.toLowerCase()}.png`}
                alt={geo.country}
                style={{ borderRadius: 3, border: "1px solid var(--vz-border-color)" }}
                onError={e => { e.target.style.display = "none"; }}
              />
            ) : (
              <i className="ri-global-line fs-2" style={{ color: "#d87844" }} />
            )}
          </Col>
          <Col md={8}>
            <div style={{ fontSize: 11, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em" }}>Agent Location</div>
            <h5 className="mb-0 fw-bold" style={{ color: "var(--vz-body-color)" }}>
              {[geo.city, geo.regionName, geo.country].filter(Boolean).join(", ") || "—"}
            </h5>
            <small style={{ color: "var(--vz-secondary-color)", fontFamily: "monospace" }}>{agentIp}</small>
          </Col>
          <Col md={3} className="text-end d-flex gap-2 justify-content-end">
            {geo.proxy   && <Pill variant="danger"><i className="ri-shield-cross-line me-1" />Proxy/VPN</Pill>}
            {geo.hosting && <Pill variant="warning"><i className="ri-server-line me-1" />Hosting</Pill>}
            {!geo.proxy && !geo.hosting && <Pill variant="success"><i className="ri-shield-check-line me-1" />Clean IP</Pill>}
          </Col>
        </Row>
      </div>

      <Row>
        <Col md={6}>
          <Section title="Location Details" icon="ri-map-pin-line">
            <DR label="Country">{geo.country} {geo.countryCode ? `(${geo.countryCode})` : ""}</DR>
            <DR label="Region">{val(geo.regionName)}</DR>
            <DR label="City">{val(geo.city)}</DR>
            <DR label="Timezone">{val(geo.timezone)}</DR>
            <DR label="Coordinates">
              {geo.lat && geo.lon ? (
                <a href={`https://www.google.com/maps?q=${geo.lat},${geo.lon}`} target="_blank" rel="noreferrer" style={{ color: "#d87844" }}>
                  {geo.lat}, {geo.lon} <i className="ri-external-link-line" />
                </a>
              ) : "—"}
            </DR>
          </Section>
        </Col>
        <Col md={6}>
          <Section title="Network Details" icon="ri-router-line">
            <DR label="ISP">{val(geo.isp)}</DR>
            <DR label="Organization">{val(geo.org)}</DR>
            <DR label="Agent IP" mono>{val(agentIp)}</DR>
            <DR label="Proxy / VPN">
              <Pill variant={geo.proxy ? "danger" : "success"}>{geo.proxy ? "Yes — Suspicious" : "No"}</Pill>
            </DR>
            <DR label="Hosting / DC">
              <Pill variant={geo.hosting ? "warning" : "success"}>{geo.hosting ? "Yes — Data Center" : "No"}</Pill>
            </DR>
          </Section>
        </Col>
      </Row>

      {geo.lat && geo.lon && (
        <div style={{ ...cardStyle, overflow: "hidden" }}>
          <div style={sectionHeaderStyle}>
            <i className="ri-map-2-line" style={{ color: "#fff" }} />
            <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Map Preview</span>
          </div>
          <iframe
            title="agent-location" width="100%" height="220" frameBorder="0"
            style={{ border: 0, display: "block" }}
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${geo.lon-0.5},${geo.lat-0.5},${geo.lon+0.5},${geo.lat+0.5}&layer=mapnik&marker=${geo.lat},${geo.lon}`}
            allowFullScreen
          />
        </div>
      )}
    </>
  );
}

/* ─── IOCs ──────────────────────────────────────────────────────── */
function IOCsTab({ iocs }) {
  if (!iocs?.length) return <Empty msg="No Indicators of Compromise found for this alert" />;

  const grouped = iocs.reduce((acc, ioc) => {
    const t = ioc.ioc_type || ioc.type || "Unknown";
    (acc[t] = acc[t] || []).push(ioc.ioc_value || ioc.value || "");
    return acc;
  }, {});

  const iconMap = { "IP": "ri-router-line", "Domain": "ri-global-line", "File Path": "ri-file-line", "Hash": "ri-fingerprint-line", "URL": "ri-link" };

  return (
    <div className="p-3">
      {Object.entries(grouped).map(([type, values]) => (
        <div key={type} className="mb-4">
          <div className="d-flex align-items-center mb-2">
            <i className={`${iconMap[type] || "ri-shield-line"} me-2`} style={{ color: "#d87844" }} />
            <span style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "#d87844" }}>{type}</span>
            <span style={{ marginLeft: 8, fontSize: 11, padding: "1px 8px", borderRadius: 20, background: "var(--vz-border-color)", color: "var(--vz-body-color)" }}>{values.length}</span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {values.map((v, i) => (
              <code key={i} style={{ background: "var(--vz-secondary-bg)", border: "1px solid var(--vz-border-color)", borderRadius: 4, padding: "4px 10px", fontSize: "0.8rem", color: "var(--vz-body-color)", wordBreak: "break-all" }}>{v}</code>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── SCA ───────────────────────────────────────────────────────── */
function SCAPanel({ scaData }) {
  if (!scaData) return <Empty msg="SCA details are not available for this alert." />;

  return (
    <>
      <Section title="SCA Check" icon="ri-shield-check-line">
        <DR label="Check ID"><code>{val(scaData.check_id)}</code></DR>
        <DR label="Check Title">{val(scaData.check_title)}</DR>
        <DR label="Result"><ResultBadge result={scaData.check_result} /></DR>
        <DR label="Reason">{val(scaData.check_reason)}</DR>
        <DR label="Command" mono>{val(scaData.check_command)}</DR>
        <DR label="Policy">{val(scaData.policy_name)}</DR>
        <DR label="Policy ID" mono>{val(scaData.policy_id)}</DR>
        <DR label="File">{val(scaData.check_file)}</DR>
        <DR label="Registry" mono>{val(scaData.check_registry)}</DR>
        <DR label="Condition">{val(scaData.check_condition)}</DR>
      </Section>

      {(scaData.rationale || scaData.remediation) && (
        <Row>
          {scaData.rationale && (
            <Col md={scaData.remediation ? 6 : 12}>
              <div style={cardStyle}>
                <div style={sectionHeaderStyle}>
                  <i className="ri-question-line" style={{ color: "#fff" }} />
                  <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Rationale</span>
                </div>
                <div style={{ padding: "14px", fontSize: "0.875rem", lineHeight: 1.6, color: "var(--vz-body-color)" }}>
                  {scaData.rationale}
                </div>
              </div>
            </Col>
          )}
          {scaData.remediation && (
            <Col md={scaData.rationale ? 6 : 12}>
              <div style={{ ...cardStyle, borderColor: "rgba(22,163,74,.3)" }}>
                <div style={{ ...sectionHeaderStyle, background: "linear-gradient(135deg, #16a34a, #22c55e)" }}>
                  <i className="ri-tools-line" style={{ color: "#fff" }} />
                  <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Remediation</span>
                </div>
                <div style={{ padding: "14px", fontSize: "0.875rem", lineHeight: 1.6, color: "var(--vz-body-color)" }}>
                  {scaData.remediation}
                </div>
              </div>
            </Col>
          )}
        </Row>
      )}
    </>
  );
}

/* ─── FIM ───────────────────────────────────────────────────────── */
function FIMPanel({ fimData }) {
  if (!fimData) return <Empty msg="File integrity details are not available for this alert." />;

  // Use correct column names from DB
  const changed = safeJSON(fimData.changed_attributes) || [];
  const perms   = safeJSON(fimData.win_perm_after)     || [];

  return (
    <>
      <Row>
        <Col md={6}>
          <Section title="File / Registry Information" icon="ri-file-line">
            <DR label="Path" mono>{val(fimData.path)}</DR>
            <DR label="Alert Type">{val(fimData.alert_type)}</DR>
            <DR label="Event"><EventBadge event={fimData.event_type} /></DR>
            <DR label="Mode">{val(fimData.mode)}</DR>
            <DR label="Architecture">{val(fimData.arch)}</DR>
            <DR label="Value Name">{val(fimData.value_name)}</DR>
            <DR label="Value Type">{val(fimData.value_type)}</DR>
            <DR label="Size">{val(fimData.size_after)}</DR>
            <DR label="Mtime Before">{fmtDate(fimData.mtime_before)}</DR>
            <DR label="Mtime After">{fmtDate(fimData.mtime_after)}</DR>
          </Section>
        </Col>
        <Col md={6}>
          <Section title="Ownership" icon="ri-user-line">
            <DR label="Owner">{val(fimData.uname_after)}</DR>
            <DR label="Group">{val(fimData.gname_after)}</DR>
            <DR label="UID" mono>{val(fimData.uid_after)}</DR>
            <DR label="GID" mono>{val(fimData.gid_after)}</DR>
          </Section>

          {(fimData.md5_before || fimData.md5_after || fimData.sha1_after || fimData.sha256_after) && (
            <Section title="Checksums" icon="ri-fingerprint-line">
              <DR label="MD5 Before"><Hash value={fimData.md5_before} /></DR>
              <DR label="MD5 After"><Hash value={fimData.md5_after} /></DR>
              <DR label="SHA1 After"><Hash value={fimData.sha1_after} /></DR>
              <DR label="SHA256 After"><Hash value={fimData.sha256_after} /></DR>
            </Section>
          )}
        </Col>
      </Row>

      {changed.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <i className="ri-refresh-line" style={{ color: "#fff" }} />
            <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Changed Attributes</span>
          </div>
          <div style={{ padding: "12px 14px" }}>
            {changed.map((a, i) => (
              <Pill key={i} variant="warning" className="me-2 mb-1">{a}</Pill>
            ))}
          </div>
        </div>
      )}

      {perms.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <i className="ri-lock-line" style={{ color: "#fff" }} />
            <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Windows Permissions</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--vz-light, rgba(0,0,0,.03))" }}>
                  {["Name","Allowed","Denied"].map(h => (
                    <th key={h} style={{ padding: "8px 14px", fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perms.map((p, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--vz-border-color)" }}>
                    <td style={{ padding: "8px 14px", color: "var(--vz-body-color)" }}>{p.name}</td>
                    <td style={{ padding: "8px 14px", color: "#16a34a", fontSize: 12 }}>{(p.allowed||[]).join(", ") || "—"}</td>
                    <td style={{ padding: "8px 14px", color: "#dc2626", fontSize: 12 }}>{(p.denied||[]).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Vulnerability ─────────────────────────────────────────────── */
function VulnerabilityPanel({ threat }) {
  const [vuln, setVuln]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!threat?.threat_id) { setLoading(false); return; }
    safeFetch(`${API_BASE_URL}/threats/vulnerabilities/${threat.threat_id}`, getAuthHeaders())
      .then(d => setVuln(d?.vulnerability || d?.data || null))
      .finally(() => setLoading(false));
  }, [threat?.threat_id]);

  if (loading) return <div className="text-center py-4"><Spinner size="sm" style={{ color: "#d87844" }} /></div>;
  if (!vuln)   return <Empty msg="Vulnerability details are not available for this alert." />;

  const refs = safeJSON(vuln.reference_urls) || [];

  return (
    <>
      <div style={{ ...cardStyle, padding: "16px 18px" }}>
        <Row className="align-items-center">
          <Col md={8}>
            <div style={{ fontSize: 11, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em" }}>CVE Identifier</div>
            <h4 className="mb-1 fw-bold" style={{ color: "#d87844", fontFamily: "monospace" }}>{vuln.cve}</h4>
            {vuln.title && <p className="mb-0" style={{ fontSize: "0.875rem", color: "var(--vz-secondary-color)" }}>{vuln.title}</p>}
          </Col>
          <Col md={4} className="text-end">
            <SevBadge severity={vuln.severity} />
            {vuln.cvss_score && (
              <div className="mt-2">
                <div style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>CVSS Score</div>
                <span style={{ fontWeight: 800, fontSize: 24, color: "#d87844" }}>{parseFloat(vuln.cvss_score).toFixed(1)}</span>
              </div>
            )}
          </Col>
        </Row>
      </div>

      <Row>
        <Col md={6}>
          <Section title="Package Info" icon="ri-archive-line">
            <DR label="Package Name">{val(vuln.package_name)}</DR>
            <DR label="Version" mono>{val(vuln.package_version)}</DR>
            <DR label="Architecture">{val(vuln.package_architecture)}</DR>
            <DR label="Condition">{val(vuln.package_condition)}</DR>
            <DR label="Fixed Version">{val(vuln.fixed_version)}</DR>
            <DR label="Fix Available">
              <Pill variant={vuln.fix_available ? "success" : "danger"}>{vuln.fix_available ? "Yes" : "No"}</Pill>
            </DR>
          </Section>
        </Col>
        <Col md={6}>
          <Section title="CVSS Vector" icon="ri-bar-chart-line">
            <DR label="Attack Vector">{val(vuln.attack_vector)}</DR>
            <DR label="Complexity">{val(vuln.attack_complexity)}</DR>
            <DR label="Privileges Req.">{val(vuln.privileges_required)}</DR>
            <DR label="User Interaction">{val(vuln.user_interaction)}</DR>
            <DR label="Exploitability">{val(vuln.exploitability)}</DR>
            <DR label="Impact Score">{val(vuln.impact_score)}</DR>
          </Section>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Section title="Vulnerability Info" icon="ri-bug-line">
            <DR label="CWE ID" mono>{val(vuln.cwe_id)}</DR>
            <DR label="Type">{val(vuln.vulnerability_type)}</DR>
            <DR label="Published">{fmtDate(vuln.published_date)}</DR>
            <DR label="Updated">{fmtDate(vuln.updated_date)}</DR>
            <DR label="Status">{vuln.status && <Pill variant={vuln.status === "open" ? "danger" : "success"}>{vuln.status}</Pill>}</DR>
            <DR label="Priority">{vuln.priority && <Pill variant={["critical","high"].includes(vuln.priority) ? "danger" : "warning"}>{vuln.priority}</Pill>}</DR>
            <DR label="Remediation">{val(vuln.remediation_status)}</DR>
          </Section>
        </Col>
        <Col md={6}>
          <Section title="Affected Agent" icon="ri-computer-line">
            <DR label="Agent ID" mono>{val(vuln.agent_id)}</DR>
            <DR label="Agent Name">{val(vuln.agent_name)}</DR>
            <DR label="Agent IP" mono>{val(vuln.agent_ip)}</DR>
            <DR label="OS">{val(vuln.agent_os)}</DR>
            <DR label="OS Version">{val(vuln.agent_os_version)}</DR>
            <DR label="Detected">{fmtDate(vuln.detected_at)}</DR>
          </Section>
        </Col>
      </Row>

      {vuln.description && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <i className="ri-file-text-line" style={{ color: "#fff" }} />
            <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Description</span>
          </div>
          <div style={{ padding: 14, fontSize: "0.875rem", lineHeight: 1.7, color: "var(--vz-body-color)" }}>
            {vuln.description}
          </div>
        </div>
      )}

      {refs.filter(Boolean).length > 0 && (
        <Section title="References" icon="ri-links-line">
          {refs.filter(Boolean).map((r, i) => (
            <DR key={i} label={`Ref ${i+1}`}>
              <a href={r} target="_blank" rel="noreferrer" style={{ color: "#d87844", wordBreak: "break-all" }}>{r}</a>
            </DR>
          ))}
        </Section>
      )}
    </>
  );
}

/* ─── Rootcheck / AgentEvent / Generic ─────────────────────────── */
function RootcheckPanel({ threat, typeData }) {
  return (
    <>
      <Section title="Rootcheck Details" icon="ri-search-eye-line">
        <DR label="Agent">{val(threat.agent_name)}</DR>
        <DR label="Agent IP" mono>{val(threat.agent_ip)}</DR>
        <DR label="Location" mono>{val(threat.log_location)}</DR>
        {typeData?.title && <DR label="Title">{val(typeData.title)}</DR>}
      </Section>
      {(typeData?.log || threat.full_log) && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <i className="ri-terminal-line" style={{ color: "#fff" }} />
            <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Full Log</span>
          </div>
          <pre style={{ margin: 0, padding: 14, fontSize: "0.78rem", background: "#0d1117", color: "#7ee787", maxHeight: 280, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {typeData?.log || threat.full_log}
          </pre>
        </div>
      )}
    </>
  );
}

function AgentEventPanel({ threat, typeData }) {
  // Parse extra_data which contains Windows event details
  const extra   = safeJSON(threat.extra_data) || {};
  const edata   = extra.event_data   || {};
  const winsys  = extra.win_system   || {};
  const subject = edata.subject      || {};
  const eventId = extra.event_id     || edata.id || "";

  const EVENT_LABELS = {
    '4624': 'User Logon',
    '4634': 'User Logoff',
    '4635': 'User Logoff',
    '4720': 'User Account Created',
    '4726': 'User Account Deleted',
    '4698': 'Scheduled Task Created',
    '4719': 'System Audit Policy Changed',
    '4724': 'Password Reset Attempted',
  };

  const logonTypes = {
    '2': 'Interactive', '3': 'Network', '4': 'Batch',
    '5': 'Service',     '7': 'Unlock',  '8': 'NetworkCleartext',
    '9': 'NewCredentials', '10': 'RemoteInteractive',
    '11': 'CachedInteractive',
  };

  return (
    <>
      {/* Event header */}
      <div style={{ ...cardStyle, padding: "14px 18px", marginBottom: 12, display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 11, background: "rgba(217,119,6,.1)", border: "1px solid rgba(217,119,6,.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <i className="ri-shield-user-line" style={{ fontSize: 20, color: "#d97706" }} />
        </div>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", margin: 0 }}>Windows Security Event</p>
          <h5 style={{ fontWeight: 700, color: "var(--vz-body-color)", margin: "2px 0 0" }}>
            {EVENT_LABELS[eventId] || threat.threat_description || "Security Event"}
            {eventId && <code style={{ marginLeft: 8, fontSize: 12, padding: "2px 8px", borderRadius: 4, background: "rgba(217,119,6,.1)", color: "#d97706", border: "1px solid rgba(217,119,6,.3)" }}>ID {eventId}</code>}
          </h5>
        </div>
      </div>

      <Row>
        <Col md={6}>
          <Section title="Event Details" icon="ri-shield-keyhole-line">
            <DR label="Event ID" mono>{val(eventId)}</DR>
            <DR label="Event Type">{val(EVENT_LABELS[eventId] || threat.threat_description)}</DR>
            <DR label="Status">{val(edata.status || winsys.keywords)}</DR>
            {edata.logon_type && (
              <DR label="Logon Type">
                {edata.logon_type} — {logonTypes[edata.logon_type] || "Unknown"}
              </DR>
            )}
            <DR label="Rule ID" mono>{val(threat.wazuh_rule_id)}</DR>
            <DR label="Severity Level">{val(threat.wazuh_rule_level)}</DR>
            <DR label="Description">{val(threat.wazuh_rule_description)}</DR>
            <DR label="Location" mono>{val(threat.log_location)}</DR>
          </Section>
        </Col>
        <Col md={6}>
          <Section title="User Information" icon="ri-user-line">
            <DR label="User">{val(threat.source_user || edata.dstuser || edata.account_name)}</DR>
            <DR label="Account Domain">{val(edata.account_domain)}</DR>
            <DR label="Security ID" mono>{val(edata.security_id)}</DR>
            <DR label="Subject Account">{val(subject.account_name)}</DR>
            <DR label="Subject Domain">{val(subject.account_domain)}</DR>
            <DR label="Subject Sec ID" mono>{val(subject.security_id)}</DR>
            <DR label="Logon ID" mono>{val(subject.logon_id || edata.logon_id)}</DR>
            <DR label="System Name">{val(edata.system_name)}</DR>
          </Section>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Section title="Agent & System" icon="ri-computer-line">
            <DR label="Agent Name">{val(threat.agent_name)}</DR>
            <DR label="Agent ID" mono>{val(threat.agent_id)}</DR>
            <DR label="Agent IP" mono>{val(threat.agent_ip)}</DR>
          </Section>
        </Col>
        <Col md={6}>
          <Section title="Network" icon="ri-router-line">
            <DR label="Source IP" mono>{val(threat.source_ip || edata.srcip)}</DR>
            <DR label="Source User">{val(threat.source_user)}</DR>
            <DR label="Workstation">{val(edata.workstationName || edata.system_name)}</DR>
            <DR label="Source Port" mono>{val(threat.source_port)}</DR>
          </Section>
        </Col>
      </Row>

      {threat.full_log && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <i className="ri-terminal-line" style={{ color: "#fff" }} />
            <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Full Event Log</span>
          </div>
          <pre style={{ margin: 0, padding: 14, fontSize: "0.78rem", background: "#0d1117", color: "#7ee787", maxHeight: 300, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {threat.full_log}
          </pre>
        </div>
      )}
    </>
  );
}


/* ─── Windows Security Event Panel ─────────────────────────────── */
function WindowsSecurityEventPanel({ threat, rawAlert }) {
  const extra = React.useMemo(() => {
    if (!threat.extra_data) return {};
    try { return typeof threat.extra_data === "object" ? threat.extra_data : JSON.parse(threat.extra_data); }
    catch { return {}; }
  }, [threat.extra_data]);

  const eventId   = extra.event_id || "";
  const eventData = extra.event_data || {};
  // eslint-disable-next-line
  const winSystem = extra.win_system || {};
  const subject   = eventData.subject || rawAlert?.data?.subject || {};

  const EVENT_LABELS = {
    "4624": "Successful Logon",
    "4625": "Failed Logon",
    "4634": "Account Logoff",
    "4647": "User Initiated Logoff",
    "4720": "User Account Created",
    "4722": "User Account Enabled",
    "4725": "User Account Disabled",
    "4726": "User Account Deleted",
    "4698": "Scheduled Task Created",
    "4719": "System Audit Policy Changed",
    "4724": "Password Reset Attempted",
  };

  const LOGON_TYPES = {
    "2": "Interactive", "3": "Network", "4": "Batch",
    "5": "Service",     "7": "Unlock",  "8": "NetworkCleartext",
    "9": "NewCredentials", "10": "RemoteInteractive",
    "11": "CachedInteractive",
  };

  const logonType = eventData.logon_type || rawAlert?.data?.logon_type;
  const dstUser   = eventData.dstuser    || rawAlert?.data?.dstuser || threat.source_user;
  const srcUser   = subject.account_name || eventData.subjectUserName;
  const srcDomain = subject.account_domain || eventData.subjectDomainName;
  const secId     = eventData.security_id || subject.security_id;
  const status    = eventData.status      || rawAlert?.data?.status;
  const sysName   = eventData.system_name || rawAlert?.data?.system_name;

  return (
    <>
      {/* Event header */}
      <div style={{ ...cardStyle, padding: "16px 18px", marginBottom: 12, display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 50, height: 50, borderRadius: 12, background: "rgba(216,120,68,.12)", border: "1px solid rgba(216,120,68,.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: "#d87844", fontFamily: "monospace" }}>{eventId}</span>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em", margin: 0 }}>Windows Security Event</p>
          <h5 style={{ fontWeight: 700, color: "var(--vz-body-color)", margin: "2px 0 0" }}>
            {EVENT_LABELS[eventId] || threat.threat_description}
          </h5>
        </div>
        {status && (
          <Pill variant={status.includes("SUCCESS") ? "success" : status.includes("FAIL") ? "danger" : "warning"}>
            {status.replace("AUDIT_", "")}
          </Pill>
        )}
      </div>

      <Row>
        <Col md={6}>
          <Section title="Event Details" icon="ri-shield-user-line">
            <DR label="Event ID" mono>{val(eventId)}</DR>
            <DR label="Event Type">{val(EVENT_LABELS[eventId] || threat.threat_description)}</DR>
            {logonType && (
              <DR label="Logon Type">
                <span style={{ fontFamily: "monospace" }}>{logonType}</span>
                {LOGON_TYPES[logonType] && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: "var(--vz-secondary-color)" }}>
                    ({LOGON_TYPES[logonType]})
                  </span>
                )}
              </DR>
            )}
            <DR label="Status">
              {status
                ? <Pill variant={status.includes("SUCCESS") ? "success" : "danger"}>{status.replace("AUDIT_","")}</Pill>
                : "—"}
            </DR>
            <DR label="System Name">{val(sysName)}</DR>
            <DR label="Security ID" mono>{val(secId)}</DR>
            <DR label="Location" mono>{val(threat.log_location)}</DR>
          </Section>
        </Col>
        <Col md={6}>
          <Section title="Account Information" icon="ri-user-line">
            <DR label="Target User">{val(dstUser)}</DR>
            <DR label="Subject Account">{val(srcUser)}</DR>
            <DR label="Subject Domain">{val(srcDomain)}</DR>
            <DR label="Subject Logon ID" mono>{val(subject.logon_id)}</DR>
            <DR label="Account Domain">{val(eventData.account_domain || rawAlert?.data?.account_domain)}</DR>
            <DR label="Account Name">{val(eventData.account_name  || rawAlert?.data?.account_name)}</DR>
          </Section>

          <Section title="Network" icon="ri-router-line">
            <DR label="Source IP" mono>{val(threat.source_ip || eventData.ipAddress || "—")}</DR>
            <DR label="Source Port" mono>{val(eventData.sourcePort)}</DR>
            <DR label="Workstation">{val(eventData.workstationName)}</DR>
          </Section>
        </Col>
      </Row>

      {/* Full log */}
      {threat.full_log && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <i className="ri-terminal-line" style={{ color: "#fff" }} />
            <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>Full Event Log</span>
          </div>
          <pre style={{ margin: 0, padding: 14, fontSize: "0.78rem", background: "#0d1117", color: "#7ee787", maxHeight: 300, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {threat.full_log}
          </pre>
        </div>
      )}
    </>
  );
}

function WindowsGenericPanel({ threat }) {
  return (
    <Row>
      <Col md={6}>
        <Section title="Agent & System" icon="ri-server-line">
          <DR label="Agent ID" mono>{val(threat.agent_id)}</DR>
          <DR label="Agent Name">{val(threat.agent_name)}</DR>
          <DR label="Agent IP" mono>{val(threat.agent_ip)}</DR>
          <DR label="Log Location" mono>{val(threat.log_location)}</DR>
        </Section>
      </Col>
      <Col md={6}>
        <Section title="User & Network" icon="ri-user-location-line">
          <DR label="Source User">{val(threat.source_user)}</DR>
          <DR label="Target User">{val(threat.destination_user)}</DR>
          <DR label="User ID" mono>{val(threat.user_id)}</DR>
          <DR label="Source IP" mono>{val(threat.source_ip)}</DR>
          <DR label="Source Port" mono>{val(threat.source_port)}</DR>
          <DR label="Dest IP" mono>{val(threat.destination_ip)}</DR>
          <DR label="Dest Port" mono>{val(threat.destination_port)}</DR>
        </Section>
      </Col>
    </Row>
  );
}

/* ─── MITRE Tab ─────────────────────────────────────────────────── */
function MITRETab({ mitre }) {
  if (!mitre?.length) return <Empty msg="No MITRE ATT&CK mappings for this alert" />;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "var(--vz-light, rgba(0,0,0,.03))" }}>
            {["Technique ID","Technique","Tactic"].map(h => (
              <th key={h} style={{ padding: "9px 14px", fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mitre.map((m, i) => (
            <tr key={i} style={{ borderBottom: "1px solid var(--vz-border-color)" }}>
              <td style={{ padding: "9px 14px" }}>
                <Pill variant="warning">{m.technique_id || "—"}</Pill>
              </td>
              <td style={{ padding: "9px 14px", color: "var(--vz-body-color)" }}>{m.technique || "—"}</td>
              <td style={{ padding: "9px 14px" }}>
                <Pill variant="primary">{m.tactic || "—"}</Pill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Compliance Tab ────────────────────────────────────────────── */
function ComplianceTab({ compliance }) {
  if (!compliance?.length) return <Empty msg="No compliance mappings for this alert" />;
  const grouped = compliance.reduce((acc, m) => {
    (acc[m.framework] = acc[m.framework] || []).push(m.control_id);
    return acc;
  }, {});
  return (
    <div className="p-3">
      <Row>
        {Object.entries(grouped).map(([fw, ids]) => (
          <Col md={4} key={fw} className="mb-3">
            <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "#d87844", marginBottom: 8 }}>{fw}</div>
            <div className="d-flex flex-wrap gap-1">
              {ids.map((id, i) => (
                <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "var(--vz-secondary-bg)", border: "1px solid var(--vz-border-color)", color: "var(--vz-body-color)" }}>{id}</span>
              ))}
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
}

/* ─── Logs Tab ──────────────────────────────────────────────────── */
function LogsTab({ threat }) {
  const hasLog     = !!threat.full_log;
  const hasDecoder = !!(threat.decoder_name || threat.decoder_parent);
  const hasPre     = !!(threat.predecoder_program || threat.predecoder_hostname || threat.predecoder_timestamp);

  if (!hasLog && !hasDecoder && !hasPre) return <Empty msg="No log data available for this alert" />;

  return (
    <>
      {hasLog && (
        <div className="p-3">
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", marginBottom: 8 }}>Full Log</div>
          <pre style={{ margin: 0, padding: 14, fontSize: "0.78rem", background: "#0d1117", color: "#7ee787", borderRadius: 6, maxHeight: 300, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {threat.full_log}
          </pre>
        </div>
      )}
      {(hasDecoder || hasPre) && (
        <div className="p-3">
          <Row>
            {hasDecoder && (
              <Col md={6}>
                <Section title="Decoder" icon="ri-code-line">
                  <DR label="Name">{val(threat.decoder_name)}</DR>
                  <DR label="Parent">{val(threat.decoder_parent)}</DR>
                </Section>
              </Col>
            )}
            {hasPre && (
              <Col md={6}>
                <Section title="Pre-Decoder" icon="ri-code-s-slash-line">
                  <DR label="Program">{val(threat.predecoder_program)}</DR>
                  <DR label="Hostname">{val(threat.predecoder_hostname)}</DR>
                  <DR label="Timestamp">{val(threat.predecoder_timestamp)}</DR>
                </Section>
              </Col>
            )}
          </Row>
        </div>
      )}
    </>
  );
}

/* ─── Main component ────────────────────────────────────────────── */
function WindowsDetails({ threat }) {
  const [activeTab,  setActiveTab]  = useState("details");
  const [typeData,   setTypeData]   = useState(null);
  const [mitre,      setMitre]      = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [iocs,       setIocs]       = useState([]);
  const [geoData,    setGeoData]    = useState(null);
  const [agentInfo,  setAgentInfo]  = useState(null);
  const [agentStats, setAgentStats] = useState(null);
  const [rawAlert,   setRawAlert]   = useState(null);
  const [loadingTab, setLoadingTab] = useState(true);
  const [loadingAgent, setLoadingAgent] = useState(false);

  const tType  = (threat.threat_type || "").toLowerCase();
  const groups = Array.isArray(threat.wazuh_rule_groups)
    ? threat.wazuh_rule_groups.map(g => g.toLowerCase())
    : [];

  const isVuln  = tType === "vulnerability";
  const isSCA   = tType === "sca" || groups.includes("sca");
  const isFIM   = tType === "file integrity" || tType === "registry"
    || tType.includes("syscheck") || groups.some(g => g.includes("syscheck"));
  const isRoot  = tType === "rootcheck" || groups.includes("rootcheck");
  const isAgent = tType === "agent event";
  // Windows Security Events stored with extra_data containing event_id
  const winEventIds = new Set(["4624","4625","4634","4647","4720","4722","4725","4726","4698","4719","4724"]);
  const extraParsed = React.useMemo(() => {
    if (!threat.extra_data) return {};
    try { return typeof threat.extra_data === "object" ? threat.extra_data : JSON.parse(threat.extra_data); }
    catch { return {}; }
  }, [threat.extra_data]);
  const isWinEvent  = !!(extraParsed.event_id && winEventIds.has(String(extraParsed.event_id)));

  const detailLabel = isVuln ? "Vulnerability" : isSCA ? "SCA Check" : isFIM ? "File Change"
    : isRoot ? "Rootcheck" : isWinEvent ? "Security Event" : isAgent ? "Agent Event" : "Details";

  useEffect(() => {
    if (!threat?.threat_id) { setLoadingTab(false); return; }

    const id      = threat.threat_id;
    const headers = getAuthHeaders();

    const subMap = {
      "sca":            `/threats/${id}/sca`,
      "file integrity": `/threats/${id}/fim`,
      "registry":       `/threats/${id}/fim`,
      "rootcheck":      `/threats/${id}/rootcheck`,
      "agent event":    `/threats/${id}/agent-event`,
    };
    const subEndpoint = subMap[tType] || (isFIM ? `/threats/${id}/fim` : null);


    Promise.all([
      safeFetch(`${API_BASE_URL}/threats/${id}/mitre`,      headers),
      safeFetch(`${API_BASE_URL}/threats/${id}/compliance`, headers),
      safeFetch(`${API_BASE_URL}/threats/${id}/iocs`,       headers),
      // safeFetch(`${API_BASE_URL}/threats/${id}/geo`,        headers), // geo commented out
      safeFetch(`${API_BASE_URL}/threats/${id}/raw`,        headers),
      subEndpoint ? safeFetch(`${API_BASE_URL}${subEndpoint}`, headers) : Promise.resolve(null),
    ]).then(([md, cd, id_res, rd, td]) => {
      const mitreArr      = md?.data      || md?.mappings  || [];
      const complianceArr = cd?.data      || cd?.mappings  || [];
      const iocsArr       = id_res?.data  || id_res?.iocs  || [];
      const typeResolved  = td ? (td.data || td.fim || td.sca || td.rootcheck || td.agent_event || null) : null;

      // ── Debug: log all loaded data ──
      console.group(`[ThreatDetails] ${threat.threat_id}`);
      console.log('threat_type   :', tType);
      console.log('rule_groups   :', groups);
      console.log('isVuln        :', isVuln);
      console.log('isSCA         :', isSCA);
      console.log('isFIM         :', isFIM);
      console.log('isRoot        :', isRoot);
      console.log('isAgent       :', isAgent);
      console.log('mitre         :', mitreArr);
      console.log('compliance    :', complianceArr);
      console.log('iocs          :', iocsArr);
      console.log('typeData (raw):', td);
      console.log('typeResolved  :', typeResolved);
      console.log('rawAlert      :', rd?.data || null);
      console.log('threat object :', threat);
      console.groupEnd();

      setMitre(mitreArr);
      setCompliance(complianceArr);
      setIocs(iocsArr);
      setRawAlert(rd?.data || null);
      setTypeData(typeResolved);
    }).finally(() => setLoadingTab(false));

    // ── Fetch agent info + geo from Flask using agent_name ──
    if (threat?.agent_name) {
      setLoadingAgent(true);
      const flaskUrl = localStorage.getItem("flaskApiUrl") || "";
      const flaskKey = localStorage.getItem("flaskApiKey") || "";
      const flaskHeaders = { "Content-Type": "application/json", "X-API-Key": flaskKey };

      // Get agent list to find public_ip + status
      fetch(`${flaskUrl}/api/wazuh/agents`, { headers: flaskHeaders })
        .then(r => r.json())
        .then(d => {
          const agent = (d.agents || []).find(a => a.name === threat.agent_name);
          console.log('[ThreatDetails] Agent info from Flask:', agent || 'NOT FOUND');
          if (agent) {
            setAgentInfo(agent);
            // Geo lookup using public_ip
            if (agent.public_ip) {
              fetch(`${flaskUrl}/api/geo/ip/${encodeURIComponent(agent.public_ip)}`, { headers: flaskHeaders })
                .then(r => r.json())
                .then(g => setGeoData(g?.geo || null))
                .catch(() => {});
            }
          }
        })
        .catch(() => {})
        .finally(() => setLoadingAgent(false));

      // Get agent threat stats from Node.js
      fetch(`${API_BASE_URL}/threats/agents/${encodeURIComponent(threat.agent_name)}/details`, { headers: getAuthHeaders() })
        .then(r => r.json())
        .then(d => {
          console.log('[ThreatDetails] Agent stats from Node:', d?.summary || 'NOT FOUND');
          if (d.success) setAgentStats(d.summary);
        })
        .catch(() => {});
    }

    // eslint-disable-next-line
  }, [threat?.threat_id]);

  const tabs = [
    { id: "details",    label: detailLabel,                         icon: "ri-file-info-line"     },
    { id: "overview",   label: "Overview",                          icon: "ri-dashboard-line"     },
    { id: "agent",     label: "Agent",          icon: "ri-computer-line"  },
    { id: "geo",       label: "Geo Location",   icon: "ri-map-pin-line"   },
    { id: "mitre",      label: `MITRE (${mitre.length})`,           icon: "ri-target-line"        },
    { id: "compliance", label: `Compliance (${compliance.length})`, icon: "ri-check-double-line"  },
    { id: "iocs",       label: `IOCs (${iocs.length})`,             icon: "ri-shield-line"        },
    { id: "logs",       label: "Logs",                              icon: "ri-terminal-box-line"  },
    ...(rawAlert ? [{ id: "raw", label: "Raw Alert", icon: "ri-code-line" }] : []),
  ];

  const tabContent = (id, content) => (
    <TabPane tabId={id}>
      <div style={{ ...cardStyle, marginTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: "none" }}>
        <div className="pt-3 px-3 pb-3">{content}</div>
      </div>
    </TabPane>
  );

  return (
    <>
      {/* Tab nav */}
      <div style={{ background: "var(--vz-secondary-bg, #fff)", border: "1px solid var(--vz-border-color)", borderRadius: "10px 10px 0 0", overflowX: "auto" }}>
        <Nav tabs style={{ borderBottom: "2px solid var(--vz-border-color)", flexWrap: "nowrap", margin: 0 }}>
          {tabs.map(t => (
            <NavItem key={t.id} style={{ whiteSpace: "nowrap" }}>
              <NavLink
                className={classnames({ active: activeTab === t.id })}
                onClick={() => setActiveTab(t.id)}
                style={{
                  cursor:      "pointer",
                  color:       activeTab === t.id ? "#d87844" : "var(--vz-secondary-color)",
                  borderColor: activeTab === t.id ? "var(--vz-border-color) var(--vz-border-color) var(--vz-secondary-bg, #fff)" : "transparent",
                  fontWeight:  activeTab === t.id ? 700 : 400,
                  fontSize:    "0.82rem",
                  padding:     "10px 16px",
                  background:  activeTab === t.id ? "var(--vz-secondary-bg, #fff)" : "transparent",
                }}
              >
                <i className={`${t.icon} me-1`} />{t.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <TabContent activeTab={activeTab}>

        {/* DETAILS */}
        {tabContent("details",
          loadingTab
            ? <div className="text-center py-4"><Spinner size="sm" style={{ color: "#d87844" }} /></div>
            : (
              <>
                {isVuln  && <VulnerabilityPanel threat={threat} />}
                {isSCA   && <SCAPanel scaData={typeData} />}
                {isFIM   && <FIMPanel fimData={typeData} />}
                {isRoot  && <RootcheckPanel threat={threat} typeData={typeData} />}
                {isAgent && <AgentEventPanel threat={threat} typeData={typeData} />}
                {isWinEvent && <WindowsSecurityEventPanel threat={threat} rawAlert={rawAlert} />}
                {!isVuln && !isSCA && !isFIM && !isRoot && !isAgent && !isWinEvent && <WindowsGenericPanel threat={threat} />}
              </>
            )
        )}

        {/* OVERVIEW */}
        {tabContent("overview",
          <Row>
            <Col md={6}>
              <Section title="Threat Information" icon="ri-alert-line">
                <DR label="Threat ID" mono>{val(threat.threat_id)}</DR>
                <DR label="Alert ID" mono>{val(threat.wazuh_alert_id)}</DR>
                <DR label="Rule ID" mono>{val(threat.wazuh_rule_id)}</DR>
                <DR label="Severity Level">{val(threat.wazuh_rule_level)}</DR>
                <DR label="Occurrences">{val(threat.wazuh_rule_firedtimes)}</DR>
                <DR label="Threat Type">{val(threat.threat_type)}</DR>
                <DR label="Status">
                  {threat.status && <Pill variant={threat.status === "Active" ? "danger" : "success"}>{threat.status}</Pill>}
                </DR>
                <DR label="Timestamp">{fmtDate(threat.timestamp)}</DR>
              </Section>
            </Col>
            <Col md={6}>
              <Section title="Asset Information" icon="ri-computer-line">
                <DR label="Asset ID" mono>{val(threat.asset_id)}</DR>
                <DR label="Asset Name">{val(threat.asset_name)}</DR>
                <DR label="Criticality">
                  {threat.asset_criticality && <SevBadge severity={threat.asset_criticality} />}
                </DR>
                <DR label="Agent ID" mono>{val(threat.agent_id)}</DR>
                <DR label="Agent Name">{val(threat.agent_name)}</DR>
                <DR label="Agent IP" mono>{val(threat.agent_ip)}</DR>
              </Section>
            </Col>

            <Col md={12}>
              <Section title="Rule Details" icon="ri-file-list-line">
                <DR label="Description">{val(threat.wazuh_rule_description)}</DR>
                <DR label="Rule Groups">
                  {(() => {
                    const grps = Array.isArray(threat.wazuh_rule_groups)
                      ? threat.wazuh_rule_groups
                      : threat.wazuh_rule_groups
                        ? String(threat.wazuh_rule_groups).replace(/[{}"]/g,"").split(",").filter(Boolean)
                        : [];
                    return grps.length
                      ? <div className="d-flex flex-wrap gap-1">{grps.map((g,i) => <Pill key={i} variant="primary">{g.trim()}</Pill>)}</div>
                      : "—";
                  })()}
                </DR>
              </Section>
            </Col>
          </Row>
        )}

        {/* AGENT */}
        {tabContent("agent",
          loadingAgent
            ? <div className="text-center py-4"><Spinner size="sm" style={{ color: "#d87844" }} /></div>
            : agentInfo
              ? (
                <Row>
                  <Col md={6}>
                    <Section title="Agent Status" icon="ri-computer-line">
                      <DR label="Name">{val(agentInfo.name)}</DR>
                      <DR label="Agent ID" mono>{val(agentInfo.id)}</DR>
                      <DR label="Status">
                        {(() => {
                          const s = (agentInfo.status || "").toLowerCase();
                          const online  = s.includes("active") || s.includes("online");
                          const offline = s.includes("disconnect") || s.includes("offline");
                          return (
                            <Pill variant={online ? "success" : offline ? "danger" : "warning"}>
                              {online ? "Online" : offline ? "Offline" : agentInfo.status}
                            </Pill>
                          );
                        })()}
                      </DR>
                      <DR label="OS">{val(agentInfo.os)}</DR>
                      <DR label="Internal IP" mono>{val(agentInfo.ip)}</DR>
                      <DR label="Public IP" mono>
                        {agentInfo.public_ip
                          ? <span style={{ color: "#0284c7", fontFamily: "monospace" }}>{agentInfo.public_ip}</span>
                          : "—"}
                      </DR>
                      <DR label="Last Seen">{agentInfo.last_updated ? new Date(agentInfo.last_updated).toLocaleString() : "—"}</DR>
                    </Section>
                  </Col>
                  <Col md={6}>
                    <Section title="Threat Statistics" icon="ri-shield-line">
                      {agentStats ? (
                        <>
                          <DR label="Total Threats"><span style={{ fontWeight: 700, color: "#d87844" }}>{agentStats.total_threats?.toLocaleString()}</span></DR>
                          <DR label="Critical"><Pill variant="danger">{agentStats.critical || 0}</Pill></DR>
                          <DR label="High"><Pill variant="warning">{agentStats.high || 0}</Pill></DR>
                          <DR label="Medium"><Pill variant="info">{agentStats.medium || 0}</Pill></DR>
                          <DR label="Low"><Pill variant="success">{agentStats.low || 0}</Pill></DR>
                          <DR label="Avg Rule Level">{val(agentStats.avg_rule_level)}</DR>
                          <DR label="Unique IPs">{val(agentStats.unique_ips)}</DR>
                        </>
                      ) : (
                        <DR label="Stats">Loading...</DR>
                      )}
                    </Section>
                  </Col>
                  {agentInfo.public_ip && geoData && (
                    <Col md={12}>
                      <Section title="Location" icon="ri-map-pin-line">
                        <Row>
                          <Col md={6}>
                            <DR label="Country">{val(geoData.country)} {geoData.countryCode ? `(${geoData.countryCode})` : ""}</DR>
                            <DR label="City">{val(geoData.city)}</DR>
                            <DR label="Region">{val(geoData.regionName)}</DR>
                            <DR label="Timezone">{val(geoData.timezone)}</DR>
                          </Col>
                          <Col md={6}>
                            <DR label="ISP">{val(geoData.isp)}</DR>
                            <DR label="Organization">{val(geoData.org)}</DR>
                            <DR label="Proxy/VPN"><Pill variant={geoData.proxy ? "danger" : "success"}>{geoData.proxy ? "Yes" : "No"}</Pill></DR>
                            <DR label="Hosting"><Pill variant={geoData.hosting ? "warning" : "success"}>{geoData.hosting ? "Yes" : "No"}</Pill></DR>
                          </Col>
                        </Row>
                      </Section>
                    </Col>
                  )}
                </Row>
              )
              : <div className="text-center py-4 text-muted">No agent info available</div>
        )}

        {/* GEO */}
        {tabContent("geo",
          loadingAgent
            ? <div className="text-center py-4"><Spinner size="sm" style={{ color: "#d87844" }} /></div>
            : !agentInfo?.public_ip
              ? (
                <div className="text-center py-5" style={{ color: "var(--vz-secondary-color)" }}>
                  <i className="ri-map-pin-off-line" style={{ fontSize: 40, opacity: .3, display: "block", marginBottom: 12 }} />
                  <p style={{ fontWeight: 600, margin: "0 0 4px" }}>No Public IP</p>
                  <p style={{ fontSize: 12, opacity: .6 }}>Run the daily IP update script to populate agent public IPs</p>
                </div>
              )
              : !geoData
                ? (
                  <div className="text-center py-5" style={{ color: "var(--vz-secondary-color)" }}>
                    <i className="ri-map-pin-off-line" style={{ fontSize: 40, opacity: .3, display: "block", marginBottom: 12 }} />
                    <p style={{ fontWeight: 600, margin: "0 0 4px" }}>No Geo Data</p>
                    <p style={{ fontSize: 12, opacity: .6 }}>{agentInfo.public_ip} — no geo data found</p>
                  </div>
                )
                : <GeoLocationPanel geo={geoData} agentIp={agentInfo.public_ip} />
        )}

        {/* MITRE */}
        {tabContent("mitre", <MITRETab mitre={mitre} />)}

        {/* COMPLIANCE */}
        {tabContent("compliance", <ComplianceTab compliance={compliance} />)}

        {/* IOCs */}
        {tabContent("iocs", <IOCsTab iocs={iocs} />)}

        {/* LOGS */}
        {tabContent("logs", <LogsTab threat={threat} />)}

        {/* RAW */}
        {/* RAW */}
{rawAlert && tabContent("raw",
  <pre style={{ margin: 0, padding: 14, fontSize: "0.78rem", background: "#0d1117", color: "#7ee787", borderRadius: 6, maxHeight: 500, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
    {JSON.stringify((({ manager, ...rest }) => rest)(rawAlert), null, 2)}
  </pre>
)}

      </TabContent>
    </>
  );
}

export default WindowsDetails;